import React, { useCallback, useEffect } from 'react';
import { Prompt } from 'react-router';
import { Container, Row, Col } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import PropTypes from 'prop-types';
import useUser from 'utils/useUser';
import { isCriticalRole, isAtRiskRole } from 'utils/accessManager/helpers';
import Button from 'components/atoms/button';
import { ON_CALL_EVENT, ON_CALL_ENDED_EVENT } from 'services/wsService';
import ConditionFilter from 'components/templates/rpm/dashboard/conditionFilter';
import RealTimePatientReadings from 'components/templates/rpm/dashboard/realTimePatientReadings';
import Sorting from 'components/templates/rpm/dashboard/realTimePatientReadingsMobile/sorting';
import RealTimePatientReadingsMobile from 'components/templates/rpm/dashboard/realTimePatientReadingsMobile';
import { isMobile, isTablet } from 'utils/deviceHelper';

const OverviewDashboardPage = ({ wsClient, wsConnected }) => {
  const location = useLocation();
  const { roleNames } = useUser();
  const activity = useSelector((state) => state.doctor);
  const device = useSelector((state) => state.user.deviceType);
  const { reviewed, unsavedComments, onCallPatients } = activity;

  const {
    doctor: {
      getActivities,
      getActivitiesCount,
      changeReviewed,
      getInsuranceTypes,
      clearComments,
      addOnCallPatient,
      removeOnCallPatient,
    },
  } = useDispatch();

  const getDoctorPatientsActivity = useCallback(() => {
    if (activity.dates.length > 0) {
      let status;
      if (isCriticalRole(roleNames)) {
        status = 'critical';
      } else if (isAtRiskRole(roleNames)) {
        status = 'risk';
      }
      const { page, action } = queryString.parse(location.search);
      getActivities({ status, page: Number(page || 1), reviewed: action });
      getActivitiesCount();
    }
  }, [activity.filters, activity.dates]);

  useEffect(() => {
    getInsuranceTypes();
    if (wsConnected) {
      wsClient.on(ON_CALL_EVENT, (data) => {
        if (data.isRpmCall) {
          addOnCallPatient(data.patientGuid);
        }
      });
      wsClient.on(ON_CALL_ENDED_EVENT, (data) => {
        if (data.isRpmCall) {
          removeOnCallPatient(data.patientGuid);
        }
      });
    }
  }, [activity.filters.clinicGuid, wsClient]);

  useEffect(() => {
    getDoctorPatientsActivity();
    return () => clearComments();
  }, [getDoctorPatientsActivity]);

  const showMobileMenu = () => {
    const menu = document.getElementById('dashboard-menu');
    menu.className = 'overview-dashboard-menu-mobile';
  };

  return (
    <>
      <Prompt
        when={!!Object.keys(unsavedComments).length}
        message="You have unsaved changes, are you sure you want to leave?"
      />
      {(isMobile(device) || isTablet(device)) && (
        <Sorting action={reviewed} toggleAction={changeReviewed} />
      )}
      <Container className="overview-dashboard-container">
        <Row>
          <Col xs={12}>
            <ConditionFilter />
          </Col>
        </Row>
        {(isMobile(device) || isTablet(device)) && (
          <Row className="overview-dashboard-filter">
            <Col xs="12">
              <Button text="Filters" handleClick={showMobileMenu} />
            </Col>
          </Row>
        )}
        <Row className="patient-container">
          <Col xs="12">
            {isMobile(device) && (
              <RealTimePatientReadingsMobile onCallPatients={onCallPatients} />
            )}
            {!isMobile(device) && (
              <RealTimePatientReadings
                onCallPatients={onCallPatients}
                device={device}
                toggleAction={changeReviewed}
              />
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
};

OverviewDashboardPage.propTypes = {
  wsClient: PropTypes.object,
  wsConnected: PropTypes.bool,
};

export default OverviewDashboardPage;
